import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { categories, hotVideos, latestUpdates } from '@/mocks/data';

const categoryNames: Record<string, string> = {
  all: '全部',
  movie: '电影',
  tv: '电视剧',
  variety: '综艺',
  anime: '动漫',
  documentary: '纪录片',
  kids: '少儿',
};

function getFilteredVideos(category: string) {
  if (category === 'all') return hotVideos;
  const catName = categoryNames[category] || '';
  return hotVideos.filter(v => v.category === catName);
}

function getFilteredUpdates(category: string) {
  if (category === 'all') return latestUpdates;
  const catName = categoryNames[category] || '';
  return latestUpdates.filter(v => v.category === catName);
}

const categoryDescriptions: Record<string, string> = {
  movie: '芒果视频免费提供最新最全的高清电影在线观看，涵盖动作、喜剧、爱情、科幻、恐怖等各种类型，替代西瓜视频免费和91视频，全部免费播放不收费。',
  tv: '芒果视频电视剧频道为您提供热门国产剧、韩剧、美剧、日剧全集免费在线观看，每日更新最新剧集，高清流畅不缓冲，是西瓜视频免费的最佳替代。',
  variety: '芒果视频综艺频道汇集热门综艺节目免费在线观看，包括音乐竞技、真人秀、脱口秀等，西瓜视频免费和91视频替代之选。',
  anime: '芒果视频动漫频道提供最新国产动漫、日本动漫免费在线观看，斗罗大陆、完美世界、斗破苍穹等热门国漫全集免费播放。',
  documentary: '芒果视频纪录片频道免费提供自然、历史、科技、人文等各类优质纪录片在线观看，拓展您的视野。',
  kids: '芒果视频少儿频道为孩子们提供安全健康的动画片、儿歌、早教内容免费在线观看，让家长放心。',
};

const categoryKeywords: Record<string, string> = {
  movie: '芒果视频免费电影,免费电影在线观看,高清电影免费,电影免费播放不收费,西瓜视频免费替代',
  tv: '芒果视频免费电视剧,电视剧免费全集,热门电视剧在线观看,电视剧免费播放,91视频替代',
  variety: '芒果视频免费综艺,综艺节目在线观看,热门综艺免费,西瓜视频免费,91在线视频替代',
  anime: '芒果视频免费动漫,国产动漫在线观看,热门动漫免费播放,西瓜视频免费替代',
  documentary: '芒果视频免费纪录片,纪录片在线观看,高清纪录片免费,西瓜视频免费替代',
  kids: '芒果视频免费少儿,儿童动画在线观看,少儿节目免费,91视频替代',
};

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug);
  const [activeTab, setActiveTab] = useState<'hot' | 'latest'>('hot');

  useEffect(() => {
    setActiveCategory(slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const catName = categoryNames[slug] || '全部';
  const description = categoryDescriptions[slug] || '芒果视频免费在线观看电影电视剧全集，替代西瓜视频免费、91视频等平台。无需付费即可享受海量影视资源，免费播放不收费，高清流畅不缓冲。';
  const keywords = categoryKeywords[slug] || '芒果视频,芒果视频免费,芒果视频在线观看,免费在线观看';

  const displayVideos = getFilteredVideos(slug);
  const displayUpdates = getFilteredUpdates(slug);

  return (
    <>
      <SEOMeta
        title={`${catName} - 芒果视频免费在线观看 | 芒果视频`}
        description={description}
        keywords={keywords}
        canonicalUrl={`/category/${slug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: catName },
              ]}
            />

            <div className="mt-6">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
                {catName} - 免费在线观看
              </h1>
              <p className="text-sm text-foreground-600 mb-6 max-w-2xl">
                {description}
              </p>

              <div className="flex items-center gap-2 flex-wrap mb-8">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-6 w-fit">
                <button
                  onClick={() => setActiveTab('hot')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeTab === 'hot'
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  热门推荐
                </button>
                <button
                  onClick={() => setActiveTab('latest')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeTab === 'latest'
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  最新更新
                </button>
              </div>

              {activeTab === 'hot' && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-product-shop="">
                  {displayVideos.map((video) => (
                    <Link
                      key={video.id}
                      to={video.link}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-2.5 md:mb-3">
                        <img
                          src={video.image}
                          alt={`${video.title} - 芒果视频免费在线观看`}
                          title={`${video.title} - 芒果视频免费在线观看`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="absolute top-2 left-2">
                          <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                            {video.badge}
                          </span>
                        </div>
                        <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 text-yellow-400 text-xs px-1.5 py-0.5 rounded">
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-[10px]"></i>
                          </span>
                          {video.rating}
                        </div>
                      </div>
                      <h3 className="text-sm md:text-base font-medium text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                        {video.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{video.category} · {video.updateTo}</p>
                    </Link>
                  ))}
                  {displayVideos.length === 0 && (
                    <div className="col-span-full text-center py-16">
                      <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <i className="ri-film-line text-4xl text-foreground-300"></i>
                      </span>
                      <p className="text-foreground-500">该分类暂无内容，请查看其他分类</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'latest' && (
                <div className="space-y-0">
                  {displayUpdates.map((item, index) => (
                    <Link
                      key={item.id}
                      to={item.link}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className={`flex items-center justify-between py-3 md:py-3.5 px-3 md:px-4 rounded-lg transition-all duration-200 cursor-pointer group ${
                        index % 2 === 0 ? 'bg-background-100' : 'bg-transparent'
                      } hover:bg-background-200`}
                    >
                      <div className="flex items-center gap-3 md:gap-4 min-w-0">
                        <span className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-bold flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="min-w-0">
                          <h3 className="text-sm md:text-base font-medium text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 truncate">
                            {item.title}
                          </h3>
                          <p className="text-xs text-foreground-500 mt-0.5">
                            <span className="bg-accent-100 text-accent-700 text-xs px-1.5 py-0.5 rounded mr-2 whitespace-nowrap">{item.category}</span>
                            {item.episode}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-foreground-400 flex-shrink-0 ml-3 whitespace-nowrap">{item.time}</span>
                    </Link>
                  ))}
                  {displayUpdates.length === 0 && (
                    <div className="text-center py-16">
                      <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <i className="ri-time-line text-4xl text-foreground-300"></i>
                      </span>
                      <p className="text-foreground-500">暂无更新内容</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}